import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Close } from "icons";
import { useLang } from "hooks";
import { UTILS } from "utils";
import { TranslationText } from "../TextTranslationContainer";
import { PriceRender } from "../DisplayPrice";
import { SplitBillTypeKeys } from "modules/dine-in/checkout/components/DineInSplitBill";
import { DiscountAction } from "store/actions";
import ConditionalRenderer from "../ConditionalRenderer";
import { Alert } from "../ErrorComponent";
import DiscountLoyaltyApply from "./discount-loyalty-apply";
import { LoyaltyAction } from "store/actions";
import useCurrentUser from "hooks/useCurrentUser";
import {
  cartHasSalePricedItem,
  voucherExcludesDiscountedItems,
} from "utils/discounts.utils";
// import { CartAction } from "store/actions";

const ContentRender = ({
  title,
  value,
  textClass = "",
  priceClass = "",
  isPrice,
  titleContentProps,
  valueContentProps,
  negative,
}) => {
  return (
    <div className="grid grid-cols-5 gap-2">
      <TranslationText
        text={title}
        className={textClass + " col-span-4 "}
        contentProps={titleContentProps}
      />
      {isPrice ? (
        <>
        <PriceRender
          price={Number(value ?? 0)}
          className={priceClass + " leading-none"}
          negative={negative}
        />
        </>
      ) : (
        <TranslationText
          text={value}
          className={priceClass + " flex justify-end"}
          contentProps={valueContentProps}
        />
      )}
    </div>
  );
};

const PaymentSummary = ({
  cartTotal,
  deliveryFee = 0,
  taxRate = 0,
  page = "checkout",
  updateCart = () => {},
  isSplitInProgress = false,
}) => {
  const { currentLang } = useLang();
  const dispatch = useDispatch();
  const { discountInfo, isDiscountApplied, message } = useSelector(
    (state) => state?.Discounts
  );
  const { selectedStore } = useSelector((state) => state?.store);
  const { discounts } = useSelector((state) => state?.brand);
  const { loyaltyApplied, loyaltyAmount } = useSelector(
    (state) => state?.Loyalty
  );
  const { ordered } = useSelector((state) => state.order);
  const { selectedTip } = useSelector((state) => state?.TipMethodsReducer);
  const { currentUser } = useCurrentUser();
  const {
    cart,
    discountAmount,
    amountAfterDiscount,
    itemsGrossAmount,
    totalOrdersGrossAmount,
    dineInServiceFee,
    orderGrossAmount,
    split,
    previousDiscountApplied,
  } = useSelector((state) => state?.cart);
  const { orderType } = useSelector((state) => state?.AppReducer);

  const [isApplyVoucherOpen, setIsApplyVoucherOpen] = useState(false);

  const totalAmount =
    page === "dine-in" ? totalOrdersGrossAmount : orderGrossAmount;
  const isPercentage = discountInfo?.discountType === "percentage";
  const content = [];

  const splitInfo = [];

  if (
    (discountInfo && isDiscountApplied) ||
    (loyaltyApplied && loyaltyAmount > 0)
  ) {
    content.push({
      title: "Original Basket Total",
      value: cartTotal,
      textClass: "line-through text-black",
      priceClass: "line-through text-black leading-none",
      isPrice: true,
    });
  }

  if (discountInfo && isDiscountApplied) {
    const label = isPercentage ? `Discount Percentage` : `Discount AED`;

    content.push({
      title: label,
      value: discountAmount,
      negative: true,
      textClass: "text-green-500 font-semibold",
      priceClass: "text-green-500 font-semibold",
      isPrice: true,
      titleContentProps: {
        discount: discountInfo?.discountAmount,
      },
    });
  }

  if (loyaltyApplied && loyaltyAmount > 0) {
    content.push({
      title: "Loyalty Points",
      value: loyaltyAmount.toFixed(2),
      negative: true,
      isPrice: true,
      textClass: "text-brand-base-primary font-medium leading-none",
      priceClass: "text-brand-base-primary font-medium leading-none",
    });
  }

  if (cart.length > 0) {
    content.push({
      title: "Basket Total",
      value: amountAfterDiscount,
      priceClass: "font-medium leading-none",
      isPrice: true,
    });
  }

  if (ordered?.orderesTotalItemsPrice > 0) {
    content.push({
      title: "Previous Total",
      value: ordered?.orderesTotalItemsPrice,
      isPrice: true,
    });
  }

  if (orderType?.EN?.name === "DELIVERY") {
    content.push({
      title: "Delivery Fee",
      value: deliveryFee,
      isPrice: true,
    });
  }

  if (page === "dine-in") {
    content.push({
      title: "Dine In Service Fee",
      value: dineInServiceFee?.calculatedAmount || 0,
      isPrice: true,
    });
  }

  if (ordered?.ordersTotalTax > 0) {
    content.push({
      title: `Previous-Added Tax`,
      titleContentProps: {
        tax: taxRate,
      },
      value: (ordered.ordersTotalTax || 0).toFixed(2),
    });
  }

  if (ordered?.totalTipAmount > 0) {
    content.push({
      title: "Previous-Added Tip",
      value: (ordered.totalTipAmount || 0).toFixed(2),
      isPrice: true,
    });
  }

  if (taxRate > 0) {
    const vat = ((itemsGrossAmount + (deliveryFee ?? 0)) * (taxRate / 100)).toFixed(2);
    content.push({
      title: `vat with value`,
      titleContentProps: {
        taxRate: taxRate,
      },
      value: vat,
      isPrice: true,
    });
  }

  if (selectedTip?.value > 0 || selectedTip?.type) {
    const tipAmount =
      selectedTip?.type === "custom"
        ? selectedTip?.value?.toFixed(2)
        : (itemsGrossAmount * selectedTip?.value)?.toFixed(2);

    content.push({
      title: selectedTip?.type === "default" ? `Tip %` : "Tip",
      value: tipAmount,
      isPrice: true,
      titleContentProps: {
        tip: (selectedTip?.value * 100)?.toFixed(0),
      },
    });
  }

  if (
    split?.amount > 0 
  ) {
    splitInfo.push({
      title: "My Share",
      value:
       split?.amount,
      isPrice: true,
    });
  }

  if (ordered?.splitPayed > 0) {
    splitInfo.push({
      title: "Split Paid",
      value: ordered.splitPayed,
    });
  }


  const getRemainingPayment = () => {
    const splitTotalAmount =  (ordered?.splitPayed > 0
      ? ordered.splitPayed
      : +split?.totalAmount ?? 0)
      return totalAmount - splitTotalAmount - (split?.already ? Number(split?.totalAmount || 0) :  Number(split?.amount || 0))
  }

  console.log({split, cn:Number(split?.remainingAmount || 0) > 0, splitedPayed: ordered?.splitPayed})

  if ((ordered?.splitPayed > 0 ) || (Number(split?.remainingAmount || 0) > 0)) {
    splitInfo.push({
      title: "Remaining Payment",
      value: split?.remainingAmount,
      isPrice: true,
    });
  }

  console.log({splitInfo})

  const handleAutoApplyEligibleDiscount = () => {
    if (discounts?.length === 0) {
      return;
    }
    // Loyalty and a voucher are mutually exclusive. If the customer has already
    // chosen to redeem loyalty there is nothing to auto-apply: this effect runs
    // on every mount, so applying a discount here would silently clear that
    // choice and the order would be submitted without the redemption.
    if (loyaltyApplied) {
      return;
    }
    // useEventTracker({ name: "APPLY_COUPON", orderType, shouldLog: true });

    // A sale-priced item blocks vouchers, so do not auto-apply one that would be refused.
    const hasSalePricedItem = cartHasSalePricedItem(cart);
    const filteredDiscounts = UTILS.getFilteredCoupons(
      discounts,
      selectedStore,
      orderType,
      true
    ).filter(
      (discount) =>
        !(hasSalePricedItem && voucherExcludesDiscountedItems(discount))
    );

    let applicableDiscount = null;
    if (filteredDiscounts?.length === 0) {
      applicableDiscount = filteredDiscounts?.[0];
    } else {
      const sortedDiscounts = filteredDiscounts.sort((a, b) => {
        // const aVal = calculateDiscountValue(a);
        // const bVal = calculateDiscountValue(b);

        // if (bVal !== aVal) return bVal - aVal;

        const aEnd = new Date(a?.endDate).getTime() || Infinity;
        const bEnd = new Date(b?.endDate).getTime() || Infinity;
        return aEnd - bEnd;
      });
      applicableDiscount = sortedDiscounts?.[0];
    }

    if (applicableDiscount?._id && currentUser?.user?.userId) {
      // A voucher is actually being applied now, so the mutually exclusive
      // loyalty redemption is dropped. This replaces the unconditional
      // removeLoyalty() that used to run merely because the brand had
      // discounts defined.
      dispatch(LoyaltyAction.removeLoyalty());

      //TODO: uncomment this is we want to only apply the discount on the whole order and remove the discount from the cart items
      // dispatch(CartAction.removeDiscountAmountFromCartItems());
      const payload = {
        brandId: selectedStore?.brandId,
        discountId: applicableDiscount?._id,
        language: applicableDiscount?.language,
        serviceType: orderType?.serviceType,
        storeId: selectedStore?._id,
        userId: currentUser?.user?.userId,
      };
      const obj = {
        payload,
        cart,
        discount: applicableDiscount,
        selectedStore,
      };
      dispatch(DiscountAction.validateDiscount(
        obj));
      dispatch(DiscountAction.validateDiscountSuccess(null));
      dispatch(DiscountAction.validateDiscountFailure(null));
    }
  };

  useEffect(() => {
    if (cartTotal > 0) {
      handleAutoApplyEligibleDiscount();
    }
  }, [cartTotal]);

  return (
    <>
      <ConditionalRenderer condition={!isSplitInProgress}>
        <DiscountLoyaltyApply
          isApplyVoucherOpen={isApplyVoucherOpen}
          setIsApplyVoucherOpen={setIsApplyVoucherOpen}
          updateCart={updateCart}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={!!message}>
        <div className="mt-3">
          <Alert type="warning" title={"Note:"} message={message} />
        </div>
      </ConditionalRenderer>
      <div className="bg-brand-light-base-primary border-[1.5px] border-brand-base-primary rounded-20x p-6 w-full text-16x flex flex-col gap-2">
        {content.map((item, idx) => (
          <ContentRender
            key={idx}
            title={item?.title}
            value={item?.value}
            textClass={item?.textClass}
            priceClass={item?.priceClass}
            isPrice={item?.isPrice}
            negative={item?.negative}
            titleContentProps={item?.titleContentProps}
            // valueContentProps={item?.valueContentProps}
          />
        ))}

        <hr className="border-t-2 border-brand-base-primary" />

        <ContentRender
          title= {taxRate === 0 ? "Total (VAT Inc.)" : "Total"}
          value={totalAmount?.toFixed(2)}
          textClass="text-brand-base-primary font-bold text-17x"
          priceClass="text-brand-base-primary font-bold text-17x"
          isPrice={true}
        />

        {splitInfo.map(
          (item, idx) =>
            item?.value > 0 && (
              <ContentRender
                key={idx}
                title={item.title}
                value={item.value}
                isPrice={true}
                negative={item?.negative}
                titleContentProps={item?.titleContentProps}
                textClass="text-brand-base-primary font-medium text-16x"
                priceClass="text-brand-base-primary font-medium text-16x"
              />
            )
        )}

        <ConditionalRenderer condition={!isSplitInProgress}>
          <div className="primary-svg-stroke flex justify-between items-center text-brand-base-primary font-medium hover:font-semibold gap-1 cursor-pointer">
            <button
              className="flex items-center gap-2"
              onClick={() => setIsApplyVoucherOpen(true)}
              disabled={Object?.keys?.(previousDiscountApplied)?.length > 0}
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path
                  d="M8 12h8M12 8v8"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <p className="m-0 flex-1 text-left">
                <TranslationText
                  text={isDiscountApplied ? "Voucher Applied" : "Apply Voucher"}
                />
              </p>
            </button>
            {Object?.keys?.(previousDiscountApplied)?.length > 0 ? null: isDiscountApplied ?  (
             <span className="cursor-pointer border text-red-500 border-red-500 rounded-full hover:scale-125" style={{
              padding: "2px",

             }}>
              <Close
              className="text-currenT"
              style={{
                width: "16px",
                height: "16px",
              }}
                onClick={() => {
                  dispatch(DiscountAction.validateDiscountFailure(null));
                  dispatch(DiscountAction.validateDiscountSuccess(null));
                  dispatch(DiscountAction.removeDiscountCode());
                  dispatch(DiscountAction.setCoponValidation(false));
                }}
              />
              </span>
            ): null}
          </div>
          {isDiscountApplied && (
            <div className={currentLang === "ar" ? "text-right" : "text-left"}>
              <TranslationText
                className="text-dark-blue-900 font-bold"
                text={
                  isPercentage
                    ? `(${discountInfo?.discountAmount}%)`
                    : `(${discountInfo?.discountAmount} AED)` + "Off"
                }
              />
              <TranslationText
                className="text-dark-blue-900 font-normal"
                text={`/${discountInfo?.discountCode}`}
              />
            </div>
          )}
        </ConditionalRenderer>
      </div>
    </>
  );
};

export default PaymentSummary;
